import { isVideoId } from '../links/routes.ts';
import { YOUTUBE_EMBED_WIDGET_ID } from './bridge.ts';

const ORIGIN = /^https?:\/\/[^/?#\s]+$/;

/**
 * The YouTube-embed escape hatch — §8's Playback tab. Needs no address (it is always
 * `youtube.com`) and no mode (it is always an iframe carrying YouTube's own chrome, never a Shaka
 * source swap: unlike PeerTube's open HLS manifest, YouTube's media URLs are signed and not meant
 * for a third-party player). So the whole setting is one switch, gating whether the "Watch on"
 * menu on the watch page offers a YouTube row at all.
 */

export interface YoutubeEmbedSettings {
  /** Off by default: this is a deliberate escape hatch, not the way the app plays video. */
  readonly enabled: boolean;
}

export const DEFAULT_YOUTUBE_EMBED_SETTINGS: YoutubeEmbedSettings = {
  enabled: false,
};

/** Total (R4): the input is a stored blob or a hand-edited one. */
export function normalizeYoutubeEmbedSettings(input: unknown): YoutubeEmbedSettings {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return DEFAULT_YOUTUBE_EMBED_SETTINGS;
  }
  const record = input as Readonly<Record<string, unknown>>;
  return { enabled: record['enabled'] === true };
}

/**
 * The `<iframe>` source — `null` for anything that is not a real video id. Whole seconds, YouTube's
 * own embed spelling; `autoplay=1` because the switch is a direct response to a click, `rel=0`
 * keeps the end screen to the uploader's own videos rather than an unrelated grid, `modestbranding=1`
 * drops the YouTube logo watermark during playback, and `playsinline=1` keeps a mobile browser from
 * taking the video to its own native fullscreen the moment it starts (`player.md`'s own reasoning
 * for why the corner player never wants that). This is the practical ceiling for "look like Visious":
 * a cross-origin iframe's own chrome — the control bar, its colours, the paused-state logo —
 * cannot be restyled from outside it, so the container framing (`.externalFrame`, matched to
 * `.player`) is the rest of what this integration can offer.
 *
 * `origin` turns on the player's message API (`enablejsapi=1`), which is how the page keeps its own
 * chapters, resume position and history moving while YouTube plays (`bridge.ts`). The player posts
 * its messages to that origin only, so it has to be the page's own; anything that is not a bare
 * `scheme://host[:port]` is left out rather than passed through, and the embed then plays untracked.
 * `widgetid` is the id every message carries, which YouTube's own script also puts in the address.
 */
export function youtubeEmbedUrl(
  videoId: string,
  startSec: number | null,
  origin: string | null = null,
): string | null {
  if (!isVideoId(videoId)) return null;
  const start = startSec !== null && Number.isFinite(startSec) && startSec > 0 ? Math.floor(startSec) : null;
  const api = origin !== null && ORIGIN.test(origin) ? origin : null;
  const params = [
    'autoplay=1',
    'rel=0',
    'modestbranding=1',
    'playsinline=1',
    ...(start === null ? [] : [`start=${start}`]),
    ...(api === null
      ? []
      : ['enablejsapi=1', `origin=${encodeURIComponent(api)}`, `widgetid=${YOUTUBE_EMBED_WIDGET_ID}`]),
  ];
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${params.join('&')}`;
}
